"use client";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useSnapshot } from "valtio/react";
import NextImage from "../../NextImage";
import { EdgenModalCreateAble } from "../../EdgenModal";
import { I18nUtil } from "@utils/i18n";
import { TabkeyMap, useCredits, useShellRouter } from "@/src/hooks";
import { ROUTER_PATH } from "@/src/config";
import Translations from "@translate/components/modal/LowCreditModal/LowCreditModal";
import { MemberKeys } from "@/src/screens/Member";
import { rootStore } from "@/src/store/rootStore";

const I18n = I18nUtil.createScoped(Translations);

export interface LowCreditModalProps {
  onClose: () => void;
  credits?: number;
  className?: string;
}

export const LowCreditModal: React.FC<LowCreditModalProps> = ({
  onClose,
  credits: propsCredits,
  className,
}) => {
  const router = useShellRouter();
  const { credits: hookCredits } = useCredits();

  const displayCredits =
    propsCredits !== undefined ? propsCredits : hookCredits;

  const handleGetUnlimitedAccess = () => {
    router.push(`${ROUTER_PATH.MEMBER}/?tab=${MemberKeys.PRICING}`);
    EdgenModalCreateAble.end(false);
  };

  const { ownerInfo } = useSnapshot(rootStore.user);

  const handleCompleteTasks = () => {
    const url = `${ROUTER_PATH.AURA_PROFILE}/${ownerInfo?.id}?tabkey=${TabkeyMap.quests}`;
    router.push(url);
    EdgenModalCreateAble.end(false);
  };

  return (
    <div className={cn("flex flex-col items-center p-6 relative", className)}>
      <Icon
        data-openreplay-label="close"
        name="close"
        size={24}
        className="p-[6px] text-content_tertiary absolute top-3 right-3 cursor-pointer"
        onClick={onClose}
      />

      {/* Header Meter UI */}
      <picture>
        <NextImage
          width={216}
          height={95}
          src={"/assets/images/low_credit-dark.webp"}
          alt="Supported by"
          className="mt-[5px] mb-[14px]"
        />
      </picture>

      {/* Content */}
      <div className="flex flex-col items-center gap-3 mt-2 text-center">
        <h2
          aria-label="Low Credit Modal h2"
          data-openreplay-label="Low Credit Modal h2 Semantic"
          id="sem-low-credit-modal-h2"
          className="headline_regular_s text-content_primary"
        >
          {I18n.t("Don't Miss the Next Alpha.")}
        </h2>
        <p className="body_m text-content_secondary px-2">
          {I18n.t(
            "You're down to %{credits} Credits. Upgrade to Pro for unlimited signals and never miss a trade.",
            { credits: displayCredits },
          )}
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-col w-full gap-2 mt-6">
        <button
          aria-label="Low Credit Modal"
          data-openreplay-label="Low Credit Modal Button"
          id="btn-low-credit-modal"
          onClick={handleGetUnlimitedAccess}
          className="w-full button_m button emphasis_button md_button"
        >
          {I18n.t("Get Unlimited Access")}
        </button>

        <button
          aria-label="Low Credit Modal"
          data-openreplay-label={`Low Credit Modal Button | ${I18n.t("Complete Tasks for Free Credits")}`}
          id="btn-low-credit-modal-2"
          onClick={handleCompleteTasks}
          className="w-full button_m text-content_primary button md_button text_button"
        >
          {I18n.t("Complete Tasks for Free Credits")}
        </button>
      </div>
    </div>
  );
};

export default LowCreditModal;
